import { useApolloClient } from '@apollo/client'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { writeAudienceChangedSinceLastSlackIRSNotification } from '../../apollo/localState/writeAudienceChangedSinceLastSlackIRSNotification'
import { isMultilangPocEnabled } from '../../constants/featureFlags'
import BottomNav from '../../containers/Page/Survey/BottomNav'
import { useFieldworkAudience } from '../../hooks/audience/useFieldworkAudience'
import { useFieldwork } from '../../hooks/useFieldwork'
import useIRSlackNotification from '../../hooks/useIRSlackNotification'
import { useIsAudienceReadOnly } from '../../hooks/useIsAudienceReadOnly'
import { useProjectId } from '../../hooks/useProjectId'
import { useSurveyId } from '../../hooks/useSurveyId'
import { AudiencePageLayout } from '../../layouts/AudiencePageLayout'
import { PanelSupplier, convertAudienceType } from '../../utils/audienceHelper'
import { useProjectType } from '../Project/Project.hooks'
import AudienceContext, {
  setAudienceDisplayLanguage,
  setNewAudienceQuestionLk
} from './Audience.context'
import { AudiencePresetPickerContainer } from './AudiencePresetPicker'
import { AudienceSummaryContainer } from './AudienceSummary'
import { CountryPickerContainer } from './CountryPicker'
import { CustomizeAudienceCriteria } from './CustomizeAudienceCriteria'
import { LanguageAllocationsContainer } from './LanguageAllocations'
import { PanelPickerContainer } from './PanelPicker'

const Audience = ({
  setShowErrorPage
}: {
  setShowErrorPage: (show: true) => void
}) => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const navigate = useNavigate()
  const { isMultiMarket, isMultiMarketV2 } = useProjectType()
  const { isReadOnly } = useIsAudienceReadOnly()
  const { postIRSlackNotification } = useIRSlackNotification()
  const [isCustomisingAudienceCriteria, setCustomisingAudienceCriteria] =
    useState(false)
  const { cache } = useApolloClient()
  const { refetch: refetchFieldworkAudience } = useFieldworkAudience()

  const { fieldwork } = useFieldwork()
  const isPureSpectrum =
    !!fieldwork?.audience[0]?.__typename &&
    convertAudienceType(fieldwork.audience[0].__typename) ===
      PanelSupplier.PURESPECTRUM

  const { dispatch } = useContext(AudienceContext)

  const toggleDialogOpen: () => void = () => {
    if (isCustomisingAudienceCriteria) {
      postIRSlackNotification()
    }
    setCustomisingAudienceCriteria(!isCustomisingAudienceCriteria)
    dispatch(setNewAudienceQuestionLk(undefined))
    // The language switch lives inside this dialog; scope its selection to the
    // dialog session so a choice made here doesn't leave the always-visible
    // main-page summary stuck on a non-primary language after the dialog closes.
    dispatch(setAudienceDisplayLanguage(undefined))
    refetchFieldworkAudience()
  }

  const routeToSurvey: () => void = () => {
    navigate(`/project/${projectId}/survey/${surveyId}/questionnaire`)
  }

  useEffect(() => {
    if (isMultiMarket || isMultiMarketV2) {
      navigate(`/project/${projectId}/survey/${surveyId}/results`)
    }
  }, [isMultiMarket, isMultiMarketV2, navigate, projectId, surveyId])

  useEffect(() => {
    cache.evict({ fieldName: 'draftAudience' })
    cache.gc()

    return () => {
      writeAudienceChangedSinceLastSlackIRSNotification(cache, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId])

  // Don't render audience components for MultiMarket projects
  if (isMultiMarket || isMultiMarketV2) {
    return null
  }

  return (
    <>
      <AudiencePageLayout
        panelPicker={<PanelPickerContainer />}
        countryPicker={
          <CountryPickerContainer setShowErrorPage={setShowErrorPage} />
        }
        languageAllocations={
          // Gated by the POC kill-switch as well as the panel check: without
          // isMultilangPocEnabled() the rest of the multi-language flow (the
          // Translations route, the Preview language switcher, and the
          // CountryPicker's stale-allocation reset) is absent, so the
          // Languages editor must not render either — otherwise production
          // PureSpectrum users could persist allocations that skew backend
          // sample distribution with no way to translate or reset them.
          isMultilangPocEnabled() && isPureSpectrum ? (
            <LanguageAllocationsContainer />
          ) : undefined
        }
        showAudienceDetails
        customiseAudience={
          !isReadOnly ? (
            <CustomizeAudienceCriteria
              isOpen={isCustomisingAudienceCriteria}
              toggleIsOpen={toggleDialogOpen}
              showButton
            />
          ) : undefined
        }
        audiencePresetPicker={
          <AudiencePresetPickerContainer
            onChoseCustomPreset={toggleDialogOpen}
          />
        }
        audienceSummary={<AudienceSummaryContainer showTitle />}
      />
      {!isReadOnly && (
        <BottomNav
          width={'calc(100% - 56px)'}
          rightButtonText="To survey"
          onClickRightButton={routeToSurvey}
        />
      )}
    </>
  )
}

export default Audience
